import { ProgCss } from "./ProgCss.Generated";

interface IDateFormatter {
    yyyy: number;
    mm: string;
    dd: string;
    hh?: string;
    mi?: string;
};

const minyr = 1875;
const maxyr = 2200;

function tryParseDate(dateStringCandidateValue: string): Date | null {
    if (!dateStringCandidateValue) {
        return null;
    }

    const mapFormat = { d: 0, m: 1, y: 2 };
    const dateStr2Array = dateStringCandidateValue.split(/[ :\-\/]/g);
    const datePart = dateStr2Array.slice(0, 3);
    const datePartFormatted = [
        +datePart[mapFormat.y],
        +datePart[mapFormat.m],
        +datePart[mapFormat.d]
    ];

    const dateTrial = new Date(
        datePartFormatted.slice(0, 3).join("-") +
        (datePartFormatted.length > 3 ? " " + datePartFormatted.slice(3).join(":") : "")
    );

    if (dateStr2Array.length > 3) {
        for (const t of dateStr2Array.slice(3)) {
            datePartFormatted.push(+t);
        };
        dateTrial.setHours(datePartFormatted[3]);
        dateTrial.setMinutes(datePartFormatted[4] || 0);
        dateTrial.setSeconds(datePartFormatted[5] || 0);
    }

    return !isNaN(dateTrial.getTime()) &&
        dateTrial.getFullYear() === datePartFormatted[0] &&
        dateTrial.getMonth() + 1 === datePartFormatted[1] &&
        dateTrial.getDate() === datePartFormatted[2] &&
        dateTrial.getFullYear() >= minyr &&
        dateTrial.getFullYear() <= maxyr
        ? dateTrial
        : null;
}

function dateFormat(date: Date, func: (dateFormatter: IDateFormatter, date: Date) => string): string {
    const padLeft = (val: number, len: number): string => "0000".substring(0, len - `${val}`.length) + val;
    return func(
        {
            yyyy: date.getFullYear(),
            mm: padLeft(date.getMonth() + 1, 2),
            dd: padLeft(date.getDate(), 2),
            hh: padLeft(date.getHours(), 2),
            mi: padLeft(date.getMinutes(), 2)
        },
        date);
}

const useTimeKey = Symbol("useTime");
declare global { interface Element { [useTimeKey]: boolean | undefined } }


function handleKey(keyEvent: KeyboardEvent): void {
    const evtTarget = keyEvent.target;
    const inputTarget = evtTarget instanceof Element ? evtTarget.closest(`input.${ProgCss.isDateField}`) : null;
    if (!(inputTarget instanceof HTMLInputElement)) {
        return;
    }
    
    const thisFld = inputTarget;
    const isCtrl =keyEvent.ctrlKey;
    const isShift =keyEvent.shiftKey;
    const isAlt =keyEvent.altKey;
    const isShiftAlt = isAlt && isShift;
    const pressedKey = keyEvent.key;
    const keys = {
        enter: "Enter",
        escape: "Escape",
        up: "ArrowUp",
        down: "ArrowDown",
        ins: "Insert",
        del: "Delete",
        one: "1"
    };

    let trydate = tryParseDate(thisFld.value.trim());

    if (pressedKey === keys.enter) {
        return;
    }

    if (isCtrl && pressedKey === keys.ins) {
        keyEvent.preventDefault();
        thisFld[useTimeKey] = true;
        const now = new Date();
        const val = trydate || now;
        if (trydate) {
            val.setHours(val.getHours() + now.getHours());
            val.setMinutes(val.getMinutes() + now.getMinutes());
        }
        setValues(thisFld, val);
    }

    if (isCtrl && keyEvent.code === "Digit1") {
        keyEvent.preventDefault();
        let date = tryParseDate(thisFld.value) || new Date();
        date.setDate(1);
        setValues(thisFld, date);
    }

    if (isCtrl && pressedKey === keys.del) {
        thisFld[useTimeKey] = false;
        if (thisFld.value !== "") {
            setValues(thisFld, tryParseDate(thisFld.value) || new Date());
        }
    }

    if (isAlt && pressedKey === keys.ins) {
        thisFld[useTimeKey] = true;
        setValues(thisFld, new Date());
    }

    if (isCtrl && isAlt && pressedKey === keys.ins) {
        thisFld[useTimeKey] = true;
        setValues(thisFld, new Date());
    }

    const delta = pressedKey === keys.up ? 1 : pressedKey === keys.down ? -1 : 0;
    if (delta === 0) {
        return;
    }

    keyEvent.preventDefault();
    const fieldValue = thisFld.value.trim();

    thisFld[useTimeKey] = /:/.test(thisFld.value);

    if (!fieldValue.length) {
        setValues(thisFld, new Date());
    } else if (trydate != null) {
        if (isShiftAlt) {
            trydate.setMinutes(trydate.getMinutes() + delta);
        } else if (isAlt) {
            trydate.setHours(trydate.getHours() + delta);
        } else if (isCtrl) {
            trydate.setMonth(trydate.getMonth() + delta);
        } else if (isShift) {
            trydate.setFullYear(trydate!.getFullYear() + delta);
        } else {
            trydate.setDate(trydate!.getDate() + delta);
        }
        setValues(thisFld, trydate!);
    }
}

function setValues(fld: HTMLInputElement, date: Date): void {
    fld.value = dateFormat(date, (o: IDateFormatter) => `${o.dd}-${o.mm}-${o.yyyy}${fld[useTimeKey] ? ` ${o.hh}:${o.mi}` : ""}`);
}

export function InitDateFieldHandling() {
    document.addEventListener("keydown", handleKey);
}
